import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { suggestTitles } from "@/lib/searchClient";

/**
 * Job-title input with suggestions from the catalog.
 *
 * Titles are free text with a long tail (2.49M distinct across 124.9M rows),
 * so guessing the exact wording is hard — "VP Sales" and "Vice President of
 * Sales" are different strings. Suggestions come ordered by how many people
 * actually hold each title, so the highest-yield wording surfaces first.
 *
 * Typing here does NOT run a search; it only fetches suggestions, which hit a
 * small precomputed dictionary (~10ms). The search still waits for Apply or
 * Enter.
 */
export default function TitleAutocomplete({ value, onChange, onEnter, placeholder, testid }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const skipNext = useRef(false);

  useEffect(() => {
    // Debounced: a suggestion lookup per keystroke is wasteful even at 10ms,
    // and out-of-order responses would make the list flicker.
    if (skipNext.current) { skipNext.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }

    let cancelled = false;
    const t = setTimeout(async () => {
      const list = await suggestTitles(q);
      if (cancelled) return;
      setSuggestions(list);
      setOpen(list.length > 0);
      setActive(-1);
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [value]);

  // Clicking anywhere else dismisses the list.
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (title) => {
    skipNext.current = true;   // picking a suggestion should not reopen the list
    onChange(title);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;   // let Enter bubble to Apply
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Escape") { setOpen(false); }
    else if (e.key === "Enter" && active >= 0) {
      // Only swallow Enter when a suggestion is highlighted; otherwise it
      // should reach the panel handler and run the search.
      e.preventDefault();
      e.stopPropagation();
      choose(suggestions[active].title);
    }
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="mt-1 h-8 text-sm"
        data-testid={testid}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {suggestions.map((s, i) => (
            <button
              key={s.title}
              type="button"
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(s.title)}
              className={`flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-xs transition-colors ${
                i === active ? "bg-surface-hover" : ""
              }`}
            >
              <span className="truncate">{s.title}</span>
              {/* How many people hold this title — the reason to pick one
                  wording over another. */}
              <span className="shrink-0 font-mono-bd text-[10px] text-muted-foreground">
                {s.count >= 1000 ? `${Math.round(s.count / 1000)}k` : s.count}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
